import type { RecordLikeProtocol } from '@lighthouse/core'

/**
 * 生成 recordPool Map 的 key
 * 格式：`${dsId}-${recordId}`
 */
export function getRecordPoolKey(dsId: string, recordId: string): string {
    return `${dsId}-${recordId}`
}

/**
 * 从 record 对象生成 key
 */
export function getRecordKey(record: RecordLikeProtocol): string {
    return getRecordPoolKey(record.dsId, record.id)
}

/**
 * RecordPool Map 类型定义
 */
export type RecordPoolMap = Map<string, RecordLikeProtocol>

/**
 * 从 Map 中按 dsId 和 recordId 查找记录
 */
export function findRecordInPool(pool: RecordPoolMap, dsId: string, recordId: string): RecordLikeProtocol | undefined {
    return pool.get(getRecordPoolKey(dsId, recordId))
}

/**
 * 从 Map 中按完整条件查找记录
 */
export function findRecordByCondition(
    pool: RecordPoolMap,
    predicate: (record: RecordLikeProtocol) => boolean
): RecordLikeProtocol | undefined {
    for (const record of pool.values()) {
        if (predicate(record)) {
            return record
        }
    }
    return undefined
}

/**
 * 从 Map 中按条件过滤记录
 */
export function filterRecordsInPool(pool: RecordPoolMap, predicate: (record: RecordLikeProtocol) => boolean): RecordLikeProtocol[] {
    const result: RecordLikeProtocol[] = []
    for (const record of pool.values()) {
        if (predicate(record)) {
            result.push(record)
        }
    }
    return result
}

/**
 * 将记录数组转换为 Map
 */
export function recordsToMap(records: RecordLikeProtocol[]): RecordPoolMap {
    const map = new Map<string, RecordLikeProtocol>()
    for (const record of records) {
        map.set(getRecordKey(record), record)
    }
    return map
}

/**
 * 将 Map 转换为记录数组
 */
export function mapToRecords(pool: RecordPoolMap): RecordLikeProtocol[] {
    return [...pool.values()]
}

/**
 * 批量添加记录到 Map（会覆盖已存在的）
 */
export function addRecordsToMap(pool: RecordPoolMap, records: RecordLikeProtocol[]): void {
    for (const record of records) {
        pool.set(getRecordKey(record), record)
    }
}

/**
 * 从 Map 中删除记录
 */
export function deleteRecordFromMap(pool: RecordPoolMap, dsId: string, recordId: string): boolean {
    return pool.delete(getRecordPoolKey(dsId, recordId))
}

/**
 * 批量删除记录
 */
export function deleteRecordsFromMap(pool: RecordPoolMap, records: RecordLikeProtocol[]): void {
    for (const record of records) {
        pool.delete(getRecordKey(record))
    }
}

/**
 * 用于 useAtomData 的 selector：从 Map 中按 dsId 和 recordId 获取记录
 * 使用方式：useAtomData(recordPoolAtom, selectRecordFromPool(dsId, recordId))
 */
export function selectRecordFromPool(dsId: string, recordId: string) {
    return (pool: RecordPoolMap): RecordLikeProtocol | undefined => {
        return pool.get(getRecordPoolKey(dsId, recordId))
    }
}
